import React, { useState } from 'react';
import { isPasswordValid } from '../../../utils/passwordPolicy';
import PasswordRequirements from './PasswordRequirements';

interface NewPasswordFormProps {
  username: string;
  handleNewPasswordSubmit: (newPassword: string) => Promise<void>;
  error: string | null;
  isLoading: boolean;
  /** Abandon the challenge and return to sign-in */
  onCancel?: () => void;
}

const NewPasswordForm: React.FC<NewPasswordFormProps> = ({
  username,
  handleNewPasswordSubmit,
  error,
  isLoading,
  onCancel
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const passwordHasError = Boolean(newPassword) && !isPasswordValid(newPassword);
  const passwordMismatch = Boolean(confirmPassword) && newPassword !== confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordHasError) {
      return; // Don't submit if password doesn't meet requirements
    }

    if (newPassword !== confirmPassword) {
      return;
    }

    await handleNewPasswordSubmit(newPassword);
  };

  return (
    <div className="w-full max-w-sm py-8 md:py-10">
      <h2 className="text-2xl font-semibold mb-1 text-slate-800 text-left">Create new password</h2>
      <p className="text-gray-500 mb-6 text-sm text-left">
        You need to set a new password for your account <strong>{username}</strong>
      </p>
      
      {error && <div className="p-3 bg-red-500/10 border-l-4 border-red-500 text-red-500 rounded mb-6 text-sm">{error}</div>}
      {passwordMismatch && <div className="p-3 bg-red-500/10 border-l-4 border-red-500 text-red-500 rounded mb-6 text-sm">Passwords don't match</div>}
      
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <label className="font-medium text-slate-800 text-sm text-left" htmlFor="newPassword">
            New password
          </label>
          <input
            className={`w-full p-3 border rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
              passwordHasError ? 'border-red-300' : 'border-slate-200'
            }`}
            type="password"
            id="newPassword"
            placeholder="Enter your new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          
          <PasswordRequirements password={newPassword} />
        </div>
        
        <div className="flex flex-col gap-2">
          <label className="font-medium text-slate-800 text-sm text-left" htmlFor="confirmPassword">
            Confirm new password
          </label>
          <input
            className={`w-full p-3 border rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
              passwordMismatch ? 'border-red-300' : 'border-slate-200'
            }`}
            type="password"
            id="confirmPassword"
            placeholder="Confirm your new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>
        
        <button 
          type="submit"
          className={`w-full py-3 px-4 bg-slate-900 text-white rounded-md text-sm font-medium transition ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-slate-800'}`}
          disabled={isLoading || passwordHasError || passwordMismatch || !newPassword || !confirmPassword}
        >
          {isLoading ? 'Setting password...' : 'Set new password'}
        </button>
      </form>
      
      {onCancel && (
        <div className="mt-6 text-center text-sm">
          <button type="button" onClick={onCancel} className="text-blue-600 hover:text-blue-700 font-medium">
            Back to sign in
          </button>
        </div>
      )}

      <div className="mt-8 p-4 border border-slate-200 rounded-md bg-slate-50">
        <p className="text-sm text-gray-600">
          <strong>Note:</strong> You're seeing this screen because this is your first login or your password was reset by an administrator.
        </p>
      </div>
    </div>
  );
};

export default NewPasswordForm;
